import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import LinearProgress from '@mui/material/LinearProgress';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import RefreshIcon from '@mui/icons-material/Refresh';

import { shouldPoll } from 'shared/pollingGate';
import AdLaunchService from 'services/AdLaunchService';
import CreativeTable from './CreativeTable';
import PreviewDrawer from './PreviewDrawer';
import BatchResult, { batchAdsetId, launchedPageIds } from './BatchResult';
import { adsetUrl } from './adsManager';
import { AdStatus, BatchStatus, CandidatesResponse, LaunchCreative, PreviewResponse, QueuedBatch } from './types';

const POLL_INTERVAL_MS = 5000;
const ALL = '__all__';
// Same status dots as Order Kitting's store tabs: green live, yellow paused.
const LAUNCH_DOT_ACTIVE = '#22C55E';
const LAUNCH_DOT_PAUSED = '#EAB308';

function errorText(err: unknown): string {
  const anyErr = err as { response?: { data?: { error?: string; message?: string } }; message?: string };
  return anyErr?.response?.data?.error || anyErr?.response?.data?.message || anyErr?.message || 'Something went wrong.';
}

export default function LaunchTab() {
  const service = useMemo(() => new AdLaunchService(), []);
  const [data, setData] = useState<CandidatesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [adTypeFilter, setAdTypeFilter] = useState(ALL);
  const [productFilter, setProductFilter] = useState(ALL);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ready' | 'launched' | 'all'>('ready');
  // Already-launched rows, fetched only when the operator asks to see them.
  // `null` is "not fetched yet" and is not the same as "there are none" — an
  // empty array would render as a confident, wrong zero.
  const [launched, setLaunched] = useState<LaunchCreative[] | null>(null);
  const [launchedLoading, setLaunchedLoading] = useState(false);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Rows lifted out of the table and frozen at the top of it. Set by clicking a
  // suggested batch — four rows scattered across seventeen pages of a sorted,
  // filtered table are otherwise selected but invisible, and the only proof of
  // what you were about to launch was a count on the launch bar.
  //
  // Deliberately NOT every selection: pinning on each tick would make rows jump
  // out from under the cursor while picking a batch by hand.
  const [pinned, setPinned] = useState<string[]>([]);
  const [templateKey, setTemplateKey] = useState('');
  // ACTIVE is what a test launch is for — a paused ad set sits there costing
  // nothing and teaching nothing — so that is the default, with the dot on the
  // switch saying so before anything is launched.
  const [launchActive, setLaunchActive] = useState(true);
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState('');

  const [batchIds, setBatchIds] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<Record<string, BatchStatus>>({});
  // The POST answer, kept rather than reduced to its batch id: it carries the
  // ad set name, the campaign and the count, so the result card can name the
  // batch the instant Launch resolves instead of a poll later.
  const [queued, setQueued] = useState<Record<string, QueuedBatch>>({});
  // Creatives whose ad this session created. The candidates list is the honest
  // source — those rows are tagged Launched in Notion and drop out of the next
  // read — but that read is a second away, and the operator is looking now.
  const [launchedNow, setLaunchedNow] = useState<Set<string>>(new Set());
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [previewError, setPreviewError] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);

  // `force` re-reads every page body from Notion. The ordinary path reuses the
  // API's cached parse of any row whose Notion timestamp has not moved, which
  // is what makes a reload seconds rather than tens of them.
  const load = useCallback(async (force = false) => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await service.getCandidates(force);
      if (!res) { setLoadError('Could not load the creatives table.'); return; }
      setData(res);
      setSelected(new Set());
      setPinned([]);
    } catch (err) {
      setLoadError(errorText(err));
    } finally {
      setLoading(false);
    }
  }, [service]);

  // The launched list is its own Notion read — four extra round trips — so it
  // is fetched once, on demand, and then only re-read when it is actually
  // being shown (a refresh right after a launch has to pick up the new rows).
  const loadLaunched = useCallback(async () => {
    setLaunchedLoading(true);
    try {
      const res = await service.getLaunched();
      setLaunched(res?.launched ?? []);
    } catch (err) {
      setLoadError(errorText(err));
    } finally {
      setLaunchedLoading(false);
    }
  }, [service]);

  useEffect(() => { load(); }, [load]);

  // Fetching is driven by what the table is showing, so switching the Status
  // filter to a view that needs launched rows is the only trigger there is.
  useEffect(() => {
    if (statusFilter === 'ready') return;
    if (launched !== null || launchedLoading) return;
    loadLaunched();
  }, [statusFilter, launched, launchedLoading, loadLaunched]);

  // Every ready creative, flattened out of the proposed groupings. The table is
  // the source of truth for what can be picked; the suggestions are only a fast
  // way to select a like-for-like four.
  const allReady: LaunchCreative[] = useMemo(() => {
    if (!data) return [];
    return [...data.batches, ...data.remainders]
      .flatMap(b => b.creatives)
      .filter(c => !launchedNow.has(c.notionPageId));
  }, [data, launchedNow]);

  // The suggestion chips are built from data.batches directly, so they need the
  // same filter — otherwise a batch that was just launched stays clickable and
  // re-selects four creatives the API would refuse.
  const suggestedBatches = useMemo(
    () => (data?.batches ?? [])
      .map(b => ({ ...b, creatives: b.creatives.filter(c => !launchedNow.has(c.notionPageId)) }))
      .filter(b => b.creatives.length),
    [data, launchedNow],
  );

  // Launched rows are shown for context — what has already gone out, and when.
  // They are never selectable; the API would refuse them anyway.
  const allRows: LaunchCreative[] = useMemo(
    () => [...allReady, ...(launched ?? [])],
    [allReady, launched],
  );

  const products = useMemo(() => [...new Set(allRows.map(c => c.product))].filter(Boolean).sort(), [allRows]);
  const adTypes = useMemo(
    () => [...new Set(allRows.map(c => c.adType || ''))].filter(Boolean).sort(),
    [allRows],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const pool = statusFilter === 'ready' ? allReady
      : statusFilter === 'launched' ? (launched ?? [])
        : allRows;
    return pool.filter(c => {
      if (adTypeFilter !== ALL && c.adType !== adTypeFilter) return false;
      if (productFilter !== ALL && c.product !== productFilter) return false;
      if (q && !c.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [allReady, allRows, launched, statusFilter, adTypeFilter, productFilter, search]);

  const chosen = useMemo(() => allReady.filter(c => selected.has(c.notionPageId)), [allReady, selected]);

  // A batch is one product and one ad type — that is what `likeForLikeKey`
  // above enforces for the template — so the moment a creative is picked
  // everything else on screen is noise: the table narrows to the selected
  // product AND ad type, and opens back up when the selection is cleared.
  //
  // The refs are what make that reversible without fighting the operator: only
  // a filter this effect applied is taken away again, so a product or ad type
  // chosen by hand survives clearing the selection. Each dimension is tracked
  // on its own, since narrowing by hand on one and by selection on the other
  // is an ordinary thing to do.
  const autoAdType = useRef<string | null>(null);
  const autoProduct = useRef<string | null>(null);

  useEffect(() => {
    const narrow = (
      values: (string | null)[],
      applied: React.MutableRefObject<string | null>,
      setFilter: (update: (prev: string) => string) => void,
    ) => {
      const distinct = new Set(values.filter(Boolean) as string[]);
      if (distinct.size === 1) {
        const [only] = [...distinct];
        applied.current = only;
        setFilter(() => only);
        return;
      }
      // Only an emptied selection lifts a filter. A selection spanning two
      // products or types can only be built by widening one by hand, and
      // re-narrowing it would undo that.
      if (chosen.length) return;
      const previous = applied.current;
      applied.current = null;
      if (previous) setFilter(prev => (prev === previous ? ALL : prev));
    };

    narrow(chosen.map(c => c.adType), autoAdType, setAdTypeFilter);
    narrow(chosen.map(c => c.product), autoProduct, setProductFilter);
  }, [chosen]);

  // Pinned rows show whatever the filters say, in the order the batch proposed
  // them. Anything pinned is removed from the flow below so it appears once.
  const pinnedRows = useMemo(() => {
    const byId = new Map(allRows.map(c => [c.notionPageId, c]));
    return pinned.map(id => byId.get(id)).filter((c): c is LaunchCreative => Boolean(c));
  }, [allRows, pinned]);

  const unpinned = useMemo(
    () => (pinned.length ? filtered.filter(c => !pinned.includes(c.notionPageId)) : filtered),
    [filtered, pinned],
  );

  const clearSelection = () => { setSelected(new Set()); setPinned([]); };

  // Stated rather than discovered later in the spend report.
  const mixedProducts = new Set(chosen.map(c => c.product)).size > 1;
  const mixedTypes = new Set(chosen.map(c => c.adType)).size > 1;

  const templatesForSelection = useMemo(() => {
    if (!data) return [];
    const types = new Set(chosen.map(c => c.adType));
    // A mixed selection has no single right ad type, so offer everything — a
    // template's settings are media-agnostic anyway.
    if (types.size !== 1) return data.templates;
    return data.templates.filter(t => t.adType === [...types][0]);
  }, [data, chosen]);

  // What the selection IS, as far as the template choice is concerned: one
  // product and one ad type, or '' for nothing / a mix. The picker is reset
  // whenever this changes — a template chosen for a DCOP batch must not still
  // be sitting in the box when the next click selects a Comforter batch. The
  // dropdown filters by ad type only, so the stale value would be a perfectly
  // valid option, the Launch button would light up, and four Comforter ads
  // would go into the DCOP testing campaign. That is the exact failure the
  // whole explicit-template design exists to prevent, so this is the one place
  // it is allowed to be sticky: within a single like-for-like selection.
  const likeForLikeKey = useMemo(() => {
    const products = new Set(chosen.map(c => c.product));
    const types = new Set(chosen.map(c => c.adType));
    return products.size === 1 && types.size === 1 ? `${[...products][0]}|${[...types][0]}` : '';
  }, [chosen]);

  // A mixed selection is keyed on its exact membership. Every mix is '' above,
  // so a template picked for one mixed batch would otherwise survive the rows
  // being swapped for an entirely different mix — still "mixed", key unchanged,
  // picker never reset — and the server only checks the ad type.
  const selectionKey = useMemo(() => {
    if (likeForLikeKey || !chosen.length) return likeForLikeKey;
    return `mixed:${chosen.map(c => c.notionPageId).sort().join(',')}`;
  }, [likeForLikeKey, chosen]);

  useEffect(() => {
    // Pre-fill with the template this product + ad type launched with last
    // time, when the API remembers one and it is still offered. Otherwise the
    // operator has to pick — an empty box, not a guess.
    const remembered = likeForLikeKey ? data?.suggested?.[likeForLikeKey] : undefined;
    setTemplateKey(remembered && data?.templates.some(t => t.key === remembered) ? remembered : '');
  }, [selectionKey, likeForLikeKey, data]);

  const activeBatchIds = useMemo(() => batchIds.filter(id => !statuses[id]?.done), [batchIds, statuses]);
  const activeRef = useRef<string[]>([]);
  activeRef.current = activeBatchIds;

  useEffect(() => {
    if (!activeBatchIds.length) return undefined;
    let cancelled = false;
    const tick = async () => {
      if (cancelled || !shouldPoll() || !activeRef.current.length) return;
      const res = await service.getBatchStatus(activeRef.current);
      if (cancelled || !res) return;
      setStatuses(prev => ({ ...prev, ...res.batches }));
    };
    const timer = setInterval(tick, POLL_INTERVAL_MS);
    tick();
    return () => { cancelled = true; clearInterval(timer); };
  }, [activeBatchIds.length, service]);

  // Batches already acted on when they settled. A ref, not state: this must
  // fire exactly once per batch, and a re-render or a late poll carrying the
  // same finished batch must not open a second tab or re-read the table.
  const settledRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const justSettled = batchIds.filter(id => statuses[id]?.done && !settledRef.current.has(id));
    if (!justSettled.length) return;
    justSettled.forEach(id => settledRef.current.add(id));

    const created = justSettled.flatMap(id => launchedPageIds(statuses[id]));
    if (created.length) {
      setLaunchedNow(prev => new Set([...prev, ...created]));
      // Notion now says Launched, so re-reading candidates drops these rows at
      // source and the session set stops mattering.
      load();
      if (launched !== null) loadLaunched();
    }

    // Opening a tab from a poll is not a user gesture, so a browser may block
    // it. That is why the card keeps the same link as a button — nothing is
    // lost when the popup never appears.
    const accountId = data?.accountId;
    for (const id of justSettled) {
      const adsetId = batchAdsetId(statuses[id]);
      if (!accountId || !adsetId || !statuses[id]?.succeeded) continue;
      window.open(adsetUrl(accountId, adsetId), '_blank', 'noopener');
      break; // one tab even if two batches land on the same tick
    }
  }, [statuses, batchIds, data, launched, load, loadLaunched]);

  const toggle = (id: string) =>
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const togglePage = (ids: string[], select: boolean) =>
    setSelected(prev => {
      const next = new Set(prev);
      ids.forEach(id => (select ? next.add(id) : next.delete(id)));
      return next;
    });

  const adStatus: AdStatus = launchActive ? 'ACTIVE' : 'PAUSED';

  const doPreview = async () => {
    setPreview(null);
    setPreviewError('');
    setPreviewOpen(true);
    try {
      setPreview(await service.getPreview(chosen.map(c => c.notionPageId), templateKey, adStatus));
    } catch (err) {
      setPreviewError(errorText(err));
    }
  };

  const doLaunch = async () => {
    setLaunching(true);
    setLaunchError('');
    try {
      const batch = await service.launch(chosen.map(c => c.notionPageId), templateKey, adStatus);
      setQueued(prev => ({ ...prev, [batch.batchId]: batch }));
      setBatchIds(prev => [...prev, batch.batchId]);
      clearSelection();
    } catch (err) {
      setLaunchError(errorText(err));
    } finally {
      setLaunching(false);
    }
  };

  if (loading && !data) return <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" sx={{ mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h6">Ready to launch</Typography>
        {data && (
          <>
            <Chip size="small" color="primary" label={`${data.counts.ready} ready`} />
            {/* Clicking it is what fetches the launched rows — the count is
                unknown until then, and inventing a zero would be a lie. */}
            <Chip
              size="small"
              variant="outlined"
              clickable
              onClick={() => setStatusFilter('launched')}
              label={launchedLoading ? 'loading launched…' : launched === null ? 'show launched' : `${launched.length} launched`}
            />
            {data.counts.blocked > 0 && (
              <Chip size="small" variant="outlined" color="error" label={`${data.counts.blocked} blocked`} />
            )}
          </>
        )}
        <Box sx={{ flex: 1 }} />
        <FormControlLabel
          control={<Switch size="small" checked={launchActive} onChange={e => setLaunchActive(e.target.checked)} />}
          label={
            // The dot is the whole signal that a launch will go live, so it
            // sits with the switch rather than in a banner further down.
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                aria-label={launchActive ? 'Launch ACTIVE' : 'Launch paused'}
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: launchActive ? LAUNCH_DOT_ACTIVE : LAUNCH_DOT_PAUSED,
                }}
              />
              <span>{launchActive ? 'Launch ACTIVE' : 'Launch paused'}</span>
            </Stack>
          }
        />
        {/* A refresh straight after a launch has to pick up the rows Notion
            has just tagged, but only if they are on screen to be picked up. */}
        <Button
          size="small"
          startIcon={<RefreshIcon />}
          onClick={() => { load(); if (launched !== null) loadLaunched(); }}
          disabled={loading}
        >
          Refresh
        </Button>
        {data?.stats && (
          <Tooltip title={
            'Notion page bodies are cached per row and reused until that row is edited. '
            + 'Re-read them all if you edited a row within the last minute — Notion stamps '
            + 'its edit times to the minute, so an edit that recent can look unchanged.'
          }>
            <Typography variant="caption" color="text.secondary">
              {data.stats.bodiesCached}/{data.stats.bodiesCached + data.stats.bodiesFetched} cached
              {' · '}
              <Link component="button" variant="caption" onClick={() => load(true)} disabled={loading} sx={{ cursor: 'pointer' }}>
                re-read all
              </Link>
            </Typography>
          </Tooltip>
        )}
      </Stack>

      {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}
      {/* Still four of one product and one ad type, oldest first — a shortcut
          for the common case, not a constraint. Anything in the table can be
          selected and launched together. */}
      {data && data.batches.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">Suggested batches — click to select</Typography>
          <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
            {suggestedBatches.map(b => {
              const ids = b.creatives.map(c => c.notionPageId);
              const isSelected = ids.every(id => selected.has(id)) && selected.size === ids.length;
              // The label has to name the batch, not just its group: DCOP has
              // six suggested Static batches and "DCOP · Static (4)" six times
              // over tells you nothing about which is which. The first
              // creative does, since the groups are ordered oldest-first.
              return (
                <Tooltip
                  key={`${b.product}|${b.batchType}|${ids[0]}`}
                  title={b.creatives.map(c => c.name).join(' · ')}
                >
                  <Chip
                    size="small"
                    color={isSelected ? 'primary' : 'default'}
                    variant={isSelected ? 'filled' : 'outlined'}
                    label={`${b.product} · ${b.batchType} — ${b.creatives[0].name} +${b.creatives.length - 1}`}
                    onClick={() => { setSelected(new Set(ids)); setPinned(ids); }}
                  />
                </Tooltip>
              );
            })}
          </Stack>
        </Box>
      )}

      <Stack direction="row" sx={{ gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <TextField
          select size="small" label="Ad type" value={adTypeFilter}
          onChange={e => setAdTypeFilter(e.target.value)} sx={{ minWidth: 150 }}
        >
          <MenuItem value={ALL}>All types</MenuItem>
          {adTypes.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </TextField>
        <TextField
          select size="small" label="Product" value={productFilter}
          onChange={e => setProductFilter(e.target.value)} sx={{ minWidth: 150 }}
        >
          <MenuItem value={ALL}>All products</MenuItem>
          {products.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
        </TextField>
        <TextField
          select size="small" label="Status" value={statusFilter}
          onChange={e => setStatusFilter(e.target.value as 'ready' | 'launched' | 'all')} sx={{ minWidth: 150 }}
        >
          <MenuItem value="ready">Ready to launch</MenuItem>
          <MenuItem value="launched">Already launched</MenuItem>
          <MenuItem value="all">All</MenuItem>
        </TextField>
        <TextField
          size="small" label="Search name" value={search}
          onChange={e => setSearch(e.target.value)} sx={{ minWidth: 200 }}
        />
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
          {filtered.length} of {statusFilter === 'ready' ? allReady.length : statusFilter === 'launched' ? (launched?.length ?? 0) : allRows.length} shown
        </Typography>
      </Stack>

      {/* Sticky, so a selection made on page 1 can still be launched from page 3. */}
      {/* Always rendered. Showing it only when something is selected made the
          whole table jump every time a checkbox was ticked. */}
      <Paper elevation={chosen.length ? 3 : 0} variant={chosen.length ? 'elevation' : 'outlined'}
        sx={{ position: 'sticky', top: 8, zIndex: 2, p: 1.5, mb: 1 }}>
          <Stack direction="row" sx={{ gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <Chip
              color={chosen.length ? 'primary' : 'default'}
              variant={chosen.length ? 'filled' : 'outlined'}
              label={chosen.length ? `${chosen.length} selected` : 'Nothing selected'}
              onDelete={chosen.length ? clearSelection : undefined}
            />
            {chosen.length > 0 && chosen.length !== 4 && (
              <Typography variant="caption" color="text.secondary">a batch is normally 4</Typography>
            )}
            {chosen.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                tick rows below, or click a suggested batch
              </Typography>
            )}
            {(mixedProducts || mixedTypes) && (
              <Chip
                size="small"
                color="warning"
                label={`mixed ${[mixedProducts && 'products', mixedTypes && 'ad types'].filter(Boolean).join(' and ')}`}
              />
            )}
            <TextField
              select size="small" label="Campaign template" value={templateKey}
              onChange={e => setTemplateKey(e.target.value)} sx={{ minWidth: 320 }}
              disabled={!chosen.length}
              error={Boolean(chosen.length) && !templateKey}
            >
              {templatesForSelection.map(t => (
                <MenuItem key={t.key} value={t.key}>{t.name} — {t.campaignName}</MenuItem>
              ))}
            </TextField>
            {/* Inline, like the other notes in this row, rather than MUI's
                `helperText`: that reserves a line INSIDE the field, which makes
                the field taller than everything beside it and rides its input
                ~11px above them. Anything shorter than the 40px input adds no
                height to a centred flex row, so the message can come and go
                without moving the sticky bar or the table under it. */}
            {chosen.length > 0 && !templateKey && (
              <Typography variant="caption" color="error">Pick a template before launching.</Typography>
            )}
            <Box sx={{ flex: 1 }} />
            <Button size="small" onClick={doPreview} disabled={launching || !templateKey || !chosen.length}>Preview</Button>
            <Button size="small" variant="contained" onClick={doLaunch} disabled={launching || !templateKey || !chosen.length}>
              {launching ? <CircularProgress size={18} /> : `Launch${chosen.length ? ` ${chosen.length}` : ''}`}
            </Button>
          </Stack>
          {(mixedProducts || mixedTypes) && (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
              These share one ad set, so their spend competes directly. Fine when you mean it — just harder
              to read than a like-for-like cohort.
            </Typography>
          )}
          {launchError && <Alert severity="error" sx={{ mt: 1 }}>{launchError}</Alert>}
      </Paper>

      {/* Directly under the button that started it. This is the only place on
          this tab a launch is reported — the History tab answers the other
          question, every launch ever rather than the one just started. */}
      {batchIds.map(id => (
        <BatchResult
          key={id}
          batchId={id}
          queued={queued[id]}
          status={statuses[id]}
          accountId={data?.accountId}
        />
      ))}

      <Paper variant="outlined">
        {/* The launched rows are a second Notion read, so the table can be
            waiting on them while the ready rows are already drawn. */}
        {launchedLoading && <LinearProgress />}
        <CreativeTable
          creatives={unpinned}
          pinnedRows={pinnedRows}
          onUnpin={() => setPinned([])}
          selectedIds={selected}
          onToggle={toggle}
          onTogglePage={togglePage}
          disabled={launching}
        />
      </Paper>


      {data && data.blocked.length > 0 && (
        <Alert severity="info" sx={{ mt: 3 }}>
          <AlertTitle>Not launchable yet ({data.blocked.length})</AlertTitle>
          {data.blocked.map(c => (
            <Box key={c.notionPageId} sx={{ mb: 0.5 }}>
              <Link href={c.notionPageUrl} target="_blank" rel="noopener">{c.name || '(untitled row)'}</Link>
              {' — '}
              <Typography component="span" variant="body2" color="text.secondary">{c.reasons.join('; ')}</Typography>
            </Box>
          ))}
        </Alert>
      )}

      <PreviewDrawer open={previewOpen} onClose={() => setPreviewOpen(false)} preview={preview} error={previewError} />
    </Box>
  );
}
